/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import {
    IComponent,
    IComponentProps,
    ICoreContext,
    IImageSettings,
    Image,
    msdyn365Commerce,
} from '@msdyn365-commerce/core';
import { CommerceProperty, SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getFallbackImageUrl, getProductPageUrlSync, ObjectExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';
import { IPayableInvoice } from '@msdyn365-commerce-modules/invoice';
import { InvoicePaidStatus, OrderInvoice, SalesInvoice } from '@msdyn365-commerce/retail-proxy';
import { IInvoiceDetailsProps, IInvoiceDetailsResources } from '../../definition-extensions/invoice-details.ext.props.autogenerated';
import moment from 'moment';

export interface IInvoiceDetailsProductsData {
    invoiceDetails: InvoiceDetailsModel;
}

export interface IInvoiceDetailsProductsProps extends IComponentProps<IInvoiceDetailsProductsData> {
    context: ICoreContext;
    className: string;
    imageSettings?: IImageSettings;
    resources: IInvoiceDetailsResources;
}

export interface IInvoiceDetailsProductsComponent extends IComponent<IInvoiceDetailsProductsProps> {}

/**
 * TODO:Below interfaces and class will be in a seprate file, due to time limitation we are keeping them here.
 */
export interface IInvoicePaymentDetails {
    totalDiscount?: number;
    subtotal?: number;
    totalCharges?: number;
    totalTax?: number;
    totalAmount?: number;
    currencyCode?: string;
}

export interface IInvoiceProductLine extends SalesInvoiceLine {
    primaryImageUrl?: string;
}

export class InvoiceDetailsModel implements IPayableInvoice, IInvoicePaymentDetails {
    public id: string;

    public invoiceDate?: Date;

    public customerAccountNumber?: string;

    public customerName?: string;

    public dueDate?: Date;

    public amountDue?: number;

    public status?: InvoicePaidStatus;

    public lines: IInvoiceProductLine[];

    public totalDiscount?: number;

    public subtotal?: number;

    public totalCharges?: number;

    public totalTax?: number;

    public totalAmount?: number;

    public currencyCode?: string;

    public extensionProperties?: CommerceProperty[];

    public constructor(invoice: OrderInvoice, invoiceDetails: SalesInvoice, products: SimpleProduct[]) {
        this.id = invoice.Id || '';
        this.invoiceDate = invoice.InvoiceDate ? new Date(invoice.InvoiceDate) : undefined;
        this.customerName = invoice.CustomerName;
        this.dueDate = invoice.InvoiceDueDate ? new Date(invoice.InvoiceDueDate) : undefined;
        this.amountDue = invoice.AmountBalance;
        this.status = invoice.InvoicePaidStatusValue;
        this.lines = (invoiceDetails.SalesInvoiceLine || []).map(line => {
            const productLine: IInvoiceProductLine = {
                ...line,
                primaryImageUrl: products.find(product => product.RecordId === line.RecordId)?.PrimaryImageUrl
            };
            return productLine;
        });

        this.totalDiscount = invoiceDetails.TotalDiscount;
        this.subtotal = invoiceDetails.InvoiceSubtotal;
        this.totalCharges = invoiceDetails.TotalCharges;
        this.totalTax = invoiceDetails.TotalTaxAmount;
        this.totalAmount = invoice.TotalAmount;
        this.currencyCode = invoice.CurrencyCode;
        this.extensionProperties = invoiceDetails.ExtensionProperties;
    }

    public get canBePaid(): boolean {
        return this.status != InvoicePaidStatus.Paid;
    }
}

const _renderProductDimensions = (className: string, resources: IInvoiceDetailsResources, product: SalesInvoiceLine): JSX.Element => {
    return (
        <div className={className}>
            {StringExtensions.isNullOrWhitespace(product.ColorName) ? null : (
                <div>{resources.invoiceProductColor.replace('{0}', product.ColorName!)}</div>
            )}
            {StringExtensions.isNullOrWhitespace(product.SizeName) ? null : (
                <div>{resources.invoiceProductSize.replace('{0}', product.SizeName!)}</div>
            )}
            {StringExtensions.isNullOrWhitespace(product.StyleName) ? null : (
                <div>{resources.invoiceProductStyle.replace('{0}', product.StyleName!)}</div>
            )}
        </div>
    );
};

const _renderProductImage = (
    className: string,
    context: ICoreContext,
    product: IInvoiceProductLine,
    imageSettings?: IImageSettings
): JSX.Element | null => {
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=315&h=315&m=6', w: 0, h: 0 },
            lg: { q: 'w=315&h=315&m=6', w: 0, h: 0 },
            xl: { q: 'w=315&h=315&m=6', w: 0, h: 0 }
        },
        lazyload: true,
        cropFocalRegion: true
    };

    if (imageSettings) {
        imageSettings.cropFocalRegion = true;
    }

    const fallbackImage = getFallbackImageUrl(product.ItemId, context.actionContext.requestContext.apiSettings);
    return (
        <Image
            requestContext={context.actionContext.requestContext}
            className={className}
            altText={product.ProductName}
            title={product.ProductName}
            src={product.primaryImageUrl || ''}
            fallBackSrc={fallbackImage}
            gridSettings={context.request.gridSettings!}
            imageSettings={imageSettings || defaultImageSettings}
            loadFailureBehavior='empty'
        />
    );
};

const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode: string | undefined) => {
    if (amount === undefined) {
        return '';
    }
    return context.cultureFormatter.formatCurrency(amount, currencyCode);
};

const getValueOfPropertyType = (extensionProperties: CommerceProperty[] | undefined) => {
    // TODO: Need to remove hard coded comparisons.
    const searchKeyValue = extensionProperties?.find(extProp => extProp.Key === 'COC Search Key Value')?.Value?.StringValue;
    if (searchKeyValue) {
        if (searchKeyValue.includes('||')) {
            return searchKeyValue
                .split('||')
                .join(' ')
                .toUpperCase();
        } else {
            return searchKeyValue.toUpperCase();
        }
    }
    return;
};

const InvoiceDetailsProducts: React.FC<IInvoiceDetailsProductsProps & IInvoiceDetailsProps<{}>> = (
    props: IInvoiceDetailsProductsProps
): JSX.Element => {
    const {
        className,
        data: { invoiceDetails },
        resources
    } = props;
    const productLineClass = `${className}__product-line`;
    const productLineInfoContainerClass = `${productLineClass}__product-info-container`;
    const productLineDetailsClass = `${productLineInfoContainerClass}__product-details`;
    const productLineSmallDetailsClass = `${productLineDetailsClass}__small-details`;
    const productLineOtherDetailsClass = `${productLineDetailsClass}__other-details`;
    return (
        <div className={className}>
            {invoiceDetails.lines.map(product => {
                const orderCreatedDate = product.ExtensionProperties?.find(ep => ep.Key === 'CREATEDATE')?.Value?.StringValue;
                const longOrderDate = orderCreatedDate && moment(orderCreatedDate, 'DD/MM/YYYY').format('MMMM DD, YYYY');
                const folioNumber = product.ExtensionProperties?.find(ep => ep.Key === 'Folio Number Value')?.Value?.StringValue;
                const originalSalesId = product.ExtensionProperties?.find(ep => ep.Key === 'ORIGNALSALESID')?.Value?.StringValue;
                const placedBy = product.ExtensionProperties?.find(ep => ep.Key === 'CREATEDBY')?.Value?.StringValue;
                let productUrl = '';
                if (!ObjectExtensions.isNullOrUndefined(product.ProductName) && !ObjectExtensions.isNullOrUndefined(product.RecordId)) {
                    productUrl = getProductPageUrlSync(product.ProductName, product.RecordId, props.context.actionContext);
                }

                return (
                    <div className={productLineClass} key={product.RecordId}>
                        <div className={productLineInfoContainerClass}>
                            {_renderProductImage(`${productLineInfoContainerClass}__product-image`, props.context, product)}
                            <div className={productLineDetailsClass}>
                                <a href={productUrl} className={`${productLineDetailsClass}__name`}>
                                    {product.ProductName}
                                </a>
                                <div className={productLineSmallDetailsClass}>
                                    {_renderProductDimensions(`${productLineSmallDetailsClass}__dimensions`, resources, product)}
                                    {/* <div className={`${productLineSmallDetailsClass}__price`}>
                                        {formatAmount(props.context, product.Price, invoiceDetails.currencyCode)}
                                    </div>
                                    <div className={`${productLineSmallDetailsClass}__quantity`}>
                                        {resources.invoiceQuantity.replace('{0}', quantity.toString())}
                                    </div> */}
                                    {/* <div className={`${productLineOtherDetailsClass}__createdate`}>
                                        {resources.createdDateLabel}:{' '}
                                        {moment(
                                            product.ExtensionProperties?.find(ep => ep.Key === 'CREATEDATE')?.Value?.StringValue,
                                            'DD/MM/YYYY'
                                        ).format('MMM DD, YYYY')}
                                    </div> */}
                                    {getValueOfPropertyType(product.ExtensionProperties) && (
                                        <div className={`${productLineOtherDetailsClass}__address`}>
                                            {resources.searchKeyLabel}: {getValueOfPropertyType(product.ExtensionProperties) || 'N/A'}
                                        </div>
                                    )}
                                    {
                                        <div className={`${productLineOtherDetailsClass}__folio-number`}>
                                            {resources.folioNumberLabel}: {folioNumber || 'N/A'}
                                        </div>
                                    }
                                </div>
                                <div className={productLineOtherDetailsClass}>
                                    {_renderProductDimensions(`${productLineOtherDetailsClass}__dimensions`, resources, product)}
                                    <div className={`${productLineOtherDetailsClass}__saleOrder`}>
                                        {`${resources.saleOrderLabel}: `}
                                        {originalSalesId || 'N/A'}
                                    </div>
                                    <div className={`${productLineOtherDetailsClass}__date`}>
                                        {/* @ts-ignore */}
                                        {resources.createdDateLabel}: {longOrderDate || 'N/A'}
                                    </div>
                                    <div className={`${productLineOtherDetailsClass}__placedBy`}>
                                        {/* @ts-ignore */}
                                        {resources.placedByLabel}: {placedBy || 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={`${productLineClass}__total-price`}>
                            {formatAmount(props.context, product.NetAmount, invoiceDetails.currencyCode)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export const InvoiceDetailsProductsComponent: React.FunctionComponent<IInvoiceDetailsProductsProps> = msdyn365Commerce.createComponent<
    //@ts-expect-error
    IInvoiceDetailsProductsComponent
>('InvoiceDetailsProductsComponent', { component: InvoiceDetailsProducts });
